import _extends from "@babel/runtime/helpers/extends";
import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage, defineMessages, useIntl, useCurrency } from '@techstyle/react-intl';
import { ProductSelectorType } from '../GenericProductAttributeSelector/constants';
import { ProductListingFilterStyle } from '../ProductListingFilter/constants';
import ReviewStars from '../ReviewStars';
import slugify from '../utils/slugify';
import { LabelType } from './constants';
var DynamicFormattedMessage = FormattedMessage;
var filterFieldLabels = defineMessages({
  // FIXME: The default `slugify` function handles this poorly because it
  // doesn't account for camel casing, so it outputs `avgreviewmin`. We should
  // update `slugify` in the next MAJOR release since it would be a breaking
  // change.
  avgReviewMin: {
    id: 'site_product_filter.avg_review_min',
    defaultMessage: 'Avg. Rating'
  },
  sort: {
    id: 'site_product_grid.sort_label',
    defaultMessage: 'Sort'
  }
});
var optionLabels = defineMessages({
  avgReviewMin: {
    id: 'site_product_filter.avg_review_min_value',
    defaultMessage: '{rating} & Up'
  }
});
var AvgRatingStars = styled(ReviewStars).withConfig({
  displayName: "createLabelProvider__AvgRatingStars",
  componentId: "sc-6mxs92-0"
})(["\n  /* FIXME: We should fix alignment in ReviewStars so this isn't necessary. */\n  vertical-align: -1px;\n"]); // transform passed in label string to add in zero width space

var addZeroWidthSpaces = label => {
  return label.replace(/\//g, '/\u200B');
};
/**
 * Category Label Renderers
 */


function getCategoryMessage(category) {
  var labelOptions = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return {
    id: "site_product_category.".concat(slugify(category.label)),
    defaultMessage: category.label
  };
}

function useCategoryLabels() {
  var intl = useIntl();
  return {
    // eslint-disable-next-line react/display-name
    renderLabel: function renderLabel(category) {
      var labelOptions = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      return /*#__PURE__*/React.createElement(DynamicFormattedMessage, getCategoryMessage(category, labelOptions));
    },
    renderLabelString: function renderLabelString(category) {
      var labelOptions = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      return intl.formatMessage(getCategoryMessage(category, labelOptions));
    }
  };
}
/**
 * Review Sort Option Label Renderers
 */


function getReviewSortMessage(option) {
  var labelOptions = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return {
    id: "site_pdp.reviews_sort_by_".concat(slugify(option.label)),
    defaultMessage: option.label
  };
}

function useReviewSortLabels() {
  var intl = useIntl();
  return {
    // eslint-disable-next-line react/display-name
    renderLabel: function renderLabel(option) {
      var labelOptions = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      return /*#__PURE__*/React.createElement(DynamicFormattedMessage, getReviewSortMessage(option, labelOptions));
    },
    renderLabelString: function renderLabelString(option) {
      var labelOptions = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      return intl.formatMessage(getReviewSortMessage(option, labelOptions));
    }
  };
}
/**
 * Filter Field Label Renderers
 */


function getFilterFieldMessage(filterField) {
  var labelOptions = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var label = filterField.label,
      field = filterField.field;
  return filterFieldLabels[field] || {
    id: "site_product_filter.".concat(slugify(field)),
    defaultMessage: label
  };
}

function useFilterFieldLabels() {
  var intl = useIntl();
  var categoryLabels = CategoryLabelProvider.useLabels();
  return {
    // eslint-disable-next-line react/display-name
    renderLabel: function renderLabel(filterField) {
      var labelOptions = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      var labelType = labelOptions.labelType;

      if (labelType === LabelType.CATEGORY_FILTER) {
        var category = filterField.category;
        return /*#__PURE__*/React.createElement(FormattedMessage, {
          id: "site_product_filter.category_style",
          defaultMessage: "{category} Style",
          values: {
            category: categoryLabels.renderLabel(category, labelOptions)
          }
        });
      }

      return /*#__PURE__*/React.createElement(DynamicFormattedMessage, getFilterFieldMessage(filterField, labelOptions));
    },
    renderLabelString: function renderLabelString(filterField) {
      var labelOptions = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      var labelType = labelOptions.labelType;

      if (labelType === LabelType.CATEGORY_FILTER) {
        var category = filterField.category;
        return intl.formatMessage({
          id: 'site_product_filter.category_style',
          defaultMessage: '{category} Style'
        }, {
          category: categoryLabels.renderLabelString(category, labelOptions)
        });
      }

      return intl.formatMessage(getFilterFieldMessage(filterField, labelOptions));
    }
  };
}
/**
 * Option Label Renderers
 */


function getOptionLabelMessage(filterField, option) {
  var labelOptions = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var field = filterField.field;
  var label = option.label || option.defaultMessage || option.value.toString();
  var filterStyle = labelOptions.filterStyle,
      selectorType = labelOptions.selectorType;
  return {
    id: "site_product_filter.".concat(slugify(field), "_").concat(slugify(label)),
    defaultMessage: filterStyle === ProductListingFilterStyle.GRID || selectorType === ProductSelectorType.GRID ? addZeroWidthSpaces(label) : label
  };
}

function useOptionLabels() {
  var intl = useIntl();

  var _useCurrency = useCurrency(),
      formatCurrency = _useCurrency.formatCurrency;

  var categoryLabels = CategoryLabelProvider.useLabels();
  var reviewSortLabels = ReviewSortLabelProvider.useLabels(); // Doesn't use RBs, so no need to have distinct renderLabel and
  // renderLabelString functions.

  var renderPriceRangeString = option => {
    var formatOptions = {
      trimZeros: true
    }; // We expect option to look like { min, max }.

    var _option$value = option.value,
        min = _option$value.min,
        max = _option$value.max;

    if (min != null) {
      if (max != null) {
        return "".concat(formatCurrency(min, formatOptions), "\u2013").concat(formatCurrency(max, formatOptions));
      } else {
        return "".concat(formatCurrency(min, formatOptions), "+");
      }
    }

    if (max != null) {
      return "".concat(formatCurrency(0, formatOptions), "\u2013").concat(formatCurrency(max, formatOptions));
    }

    return '';
  };

  return {
    // eslint-disable-next-line react/display-name
    renderLabel: function renderLabel(filterField, option) {
      var labelOptions = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
      var labelType = labelOptions.labelType,
          whiteSpace = labelOptions.whiteSpace;

      if (labelType === LabelType.CATEGORY_FILTER || labelType === LabelType.CATEGORY_FILTER_TAG) {
        // It's a category.
        return categoryLabels.renderLabel(option, labelOptions);
      } else if (labelType === LabelType.REVIEW_SORT_DROPDOWN) {
        return reviewSortLabels.renderLabel(option, labelOptions);
      }

      switch (filterField.field) {
        case 'band_size':
        case 'cup_size':
          return option.label || option.value;

        case 'avgReviewMin':
          return /*#__PURE__*/React.createElement(DynamicFormattedMessage, _extends({}, optionLabels.avgReviewMin, {
            values: {
              rating: labelType === LabelType.PRODUCT_FILTER ? /*#__PURE__*/React.createElement(AvgRatingStars, {
                rating: option.value
              }) : option.value.toFixed(1)
            }
          }));

        case 'price':
          return renderPriceRangeString(option);
      }

      return /*#__PURE__*/React.createElement(DynamicFormattedMessage, _extends({}, getOptionLabelMessage(filterField, option, labelOptions), {
        whiteSpace: whiteSpace
      }));
    },
    renderLabelString: function renderLabelString(filterField, option) {
      var labelOptions = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
      var labelType = labelOptions.labelType;

      if (labelType === LabelType.CATEGORY_FILTER || labelType === LabelType.CATEGORY_FILTER_TAG) {
        // It's a category.
        return categoryLabels.renderLabelString(option, labelOptions);
      } else if (labelType === LabelType.REVIEW_SORT_DROPDOWN) {
        return reviewSortLabels.renderLabelString(option, labelOptions);
      }

      switch (filterField.field) {
        case 'band_size':
        case 'cup_size':
          return option.value;

        case 'avgReviewMin':
          return intl.formatMessage(optionLabels.avgReviewMin, {
            rating: option.value.toFixed(1)
          });

        case 'price':
          return renderPriceRangeString(option);
      }

      return intl.formatMessage(getOptionLabelMessage(filterField, option, labelOptions));
    }
  };
}

export default function createLabelProvider() {
  var displayName = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'LabelProvider';
  var useDefaultLabels = arguments.length > 1 ? arguments[1] : undefined;
  var Context = /*#__PURE__*/React.createContext({
    useLabels: useDefaultLabels
  });

  function Provider(_ref) {
    var children = _ref.children,
        useLabels = _ref.useLabels;
    var value = useMemo(() => ({
      useLabels
    }), [useLabels]);
    return /*#__PURE__*/React.createElement(Context.Provider, {
      value: value
    }, children);
  }

  Provider.displayName = "".concat(displayName, ".Provider");
  Provider.propTypes = {
    children: PropTypes.node,
    useLabels: PropTypes.func
  };

  var useLabels = () => {
    var _useContext = useContext(Context),
        useLabels = _useContext.useLabels; // If you want to wrap the defaults, those are provided to the custom hook.


    return useLabels({
      useDefaultLabels
    });
  };

  Provider.Context = Context;
  Provider.useLabels = useLabels;
  return Provider;
}
export var CategoryLabelProvider = createLabelProvider('CategoryLabels', useCategoryLabels);
export var FilterFieldLabelProvider = createLabelProvider('FilterFieldLabels', useFilterFieldLabels);
export var OptionLabelProvider = createLabelProvider('OptionLabels', useOptionLabels);
export var ReviewSortLabelProvider = createLabelProvider('ReviewSortLabels', useReviewSortLabels);